import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TbChevronRight, TbFileImport, TbPlus } from 'react-icons/tb';

import { ModalComponent, UiComponent } from 'components';
import { IQuestion, QuestionType } from 'types/forms';
import Question from './Question';
import useForms from './useForms';
import useLoadForms from './useLoadForms';
import * as S from './style';

const TYPE_NAME: Record<QuestionType, string> = {
  additional: '추가 정보',
  feedback: '피드백',
};

interface Props {
  formData: { title?: string; questions?: IQuestion[] };
  type: QuestionType;
  onSave: (title: string, questions: IQuestion[]) => void;
  canSave?: boolean;
}

function Forms(props: Props) {
  const {
    title,
    setTitle,
    questions,
    focusId,
    handleAddQuestion,
    handleDuplicateQuestion,
    handleRemoveQuestion,
    handleMoveQuestion,
    handleSave,
    load,
    handleChangeQuestion,
  } = useForms(props);

  const { data, isOpenModal, handleClose, handleSelect, handleOpen } =
    useLoadForms({
      load,
      type: props.type,
    });

  const requiredCount = questions.filter((q) => q.isRequired).length;

  return (
    <div css={S.wrap}>
      <div css={S.header}>
        <p css={S.headerMeta}>
          질문 <strong>{questions.length}</strong>개
          {requiredCount > 0 && ` · 필수 ${requiredCount}개`}
        </p>
        <UiComponent.Button variant="border" color="grey" onClick={handleOpen}>
          <TbFileImport size={16} />
          템플릿 불러오기
        </UiComponent.Button>
      </div>

      <div css={S.card(false)}>
        <input
          type="text"
          css={S.titleInput}
          placeholder={`${TYPE_NAME[props.type]} 제목을 입력해주세요`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <p css={S.titleHint}>손잡이를 드래그해 질문 순서를 바꿀 수 있어요.</p>
      </div>

      <DndProvider backend={HTML5Backend}>
        {questions.map((item, index) => (
          <Question
            key={item.id}
            index={index}
            total={questions.length}
            question={item}
            autoFocus={item.id === focusId}
            onChange={handleChangeQuestion}
            onRemove={handleRemoveQuestion}
            onDuplicate={handleDuplicateQuestion}
            onMove={handleMoveQuestion}
          />
        ))}
      </DndProvider>

      <button type="button" css={S.addQuestion} onClick={handleAddQuestion}>
        <TbPlus size={18} />
        질문 추가
      </button>

      {props.canSave !== false && (
        <div css={S.actionBar}>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            저장하기
          </UiComponent.Button>
        </div>
      )}

      {isOpenModal && (
        <ModalComponent.Portal>
          <ModalComponent.Background>
            <ModalComponent.Modal
              title={`${TYPE_NAME[props.type]} 템플릿 불러오기`}
              handleClose={handleClose}
              footer={<></>}
            >
              <div css={S.loadList}>
                <p css={S.loadHint}>
                  선택하면 지금 작성 중인 내용이 템플릿 내용으로 바뀌어요.
                </p>
                {data.length === 0 ? (
                  <p css={S.loadEmpty}>등록된 템플릿이 없어요.</p>
                ) : (
                  data.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      css={S.loadItem}
                      onClick={() => handleSelect(d)}
                    >
                      {d.title}
                      <TbChevronRight size={18} />
                    </button>
                  ))
                )}
              </div>
            </ModalComponent.Modal>
          </ModalComponent.Background>
        </ModalComponent.Portal>
      )}
    </div>
  );
}

export default Forms;
